import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { SortableHeader } from '@/client/components/table-utils';
import { Badge } from '@/client/components/ui/badge';
import { Progress } from '@/client/components/ui/progress';

interface RDSInstance {
  identifier: string;
  status: string;
  engine: string;
  engineVersion: string;
  instanceClass: string;
  allocatedStorage: number;
  endpoint: {
    address: string;
    port: number;
  } | null;
  availabilityZone: string;
  multiAZ: boolean;
  storageType: string;
  iops?: number;
}

interface RDSMetrics {
  identifier: string;
  cpu?: number;
  connections?: number;
  freeableMemory?: number;
  freeStorageSpace?: number;
  readIOPS?: number;
  writeIOPS?: number;
}

interface RDSInstanceWithMetrics extends RDSInstance {
  metrics?: RDSMetrics;
}

interface RDSResponse {
  instances: RDSInstance[];
  cacheInfo?: { cachedAt: string };
}

interface MetricsResponse {
  metrics: RDSMetrics[];
  cacheInfo?: { cachedAt: string };
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'aws/rds-health',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: RDSHealthComponent,
});

const columnHelper = createColumnHelper<RDSInstanceWithMetrics>();

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'available' ? 'default' :
                  status === 'stopped' ? 'secondary' :
                  ['failed', 'storage-full'].includes(status) ? 'destructive' :
                  'outline';

  return (
    <Badge variant={variant} className={status === 'available' ? 'bg-green-600' : ''}>
      {status}
    </Badge>
  );
}

function CPUCell({ cpu }: { cpu?: number }) {
  if (cpu === undefined) return <span className="text-muted-foreground">-</span>;

  const color = cpu > 80 ? 'bg-red-500' : cpu > 60 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <Progress value={cpu} className="h-2 w-16" indicatorClassName={color} />
      <span className="text-sm">{cpu}%</span>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}


const columns = [
  columnHelper.accessor('identifier', {
    header: ({ column }) => <SortableHeader column={column}>Instance</SortableHeader>,
    cell: (info) => {
      const id = info.getValue();
      const truncated = id.length > 20 ? `${id.slice(0, 17)}...` : id;
      return <span className="font-mono text-sm" title={id}>{truncated}</span>;
    },
  }),
  columnHelper.accessor('status', {
    header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  columnHelper.accessor((row) => row.metrics?.cpu, {
    id: 'cpu',
    header: ({ column }) => <SortableHeader column={column}>CPU</SortableHeader>,
    cell: (info) => <CPUCell cpu={info.getValue()} />,
  }),
  columnHelper.accessor((row) => row.metrics?.connections, {
    id: 'connections',
    header: ({ column }) => <SortableHeader column={column}>Conns</SortableHeader>,
    cell: (info) => {
      const val = info.getValue();
      return val !== undefined ? val : <span className="text-muted-foreground">-</span>;
    },
  }),
  columnHelper.accessor((row) => row.metrics?.freeableMemory, {
    id: 'memory',
    header: ({ column }) => <SortableHeader column={column}>Free Memory</SortableHeader>,
    cell: (info) => {
      const val = info.getValue();
      return val !== undefined ? formatBytes(val) : <span className="text-muted-foreground">-</span>;
    },
  }),
  columnHelper.accessor('engine', {
    header: ({ column }) => <SortableHeader column={column}>Engine</SortableHeader>,
    cell: (info) => info.getValue(),
  }),
];


function RDSHealthComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<RDSInstance[]>([]);
  const [metrics, setMetrics] = useState<RDSMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheInfo, setCacheInfo] = useState<{ cachedAt: string } | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'identifier', desc: false }]);

  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      if (forceRefresh) {
        await Promise.all([
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:rds:instances' }),
          }),
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:rds:metrics' }),
          }),
        ]);
      }

      const [instancesRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/aws/rds/instances`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/aws/rds/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!instancesRes.ok) {
        const err = await instancesRes.json();
        throw new Error(err.message || 'Failed to fetch RDS instances');
      }

      const instancesData: RDSResponse = await instancesRes.json();
      setInstances(instancesData.instances);
      setCacheInfo(instancesData.cacheInfo ?? null);

      if (metricsRes.ok) {
        const metricsData: MetricsResponse = await metricsRes.json();
        setMetrics(metricsData.metrics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAccessTokenSilently]);

  // Merge instances with metrics
  const instancesWithMetrics: RDSInstanceWithMetrics[] = useMemo(() => {
    const metricsMap = new Map(metrics.map((m) => [m.identifier, m]));
    return instances.map((instance) => ({
      ...instance,
      metrics: metricsMap.get(instance.identifier),
    }));
  }, [instances, metrics]);

  const table = useReactTable({
    data: instancesWithMetrics,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">RDS Health</h3>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4 mb-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          <div className="flex justify-end items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => fetchData(true)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Force Refresh
            </Button>
          </div>

          {loading && instances.length === 0 && <p>Loading RDS instances...</p>}

          {!loading && !error && instances.length === 0 && (
            <p>No RDS instances found.</p>
          )}

          {instances.length > 0 && (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate({ to: '/aws/rds/$instanceId', params: { instanceId: row.original.identifier } })}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {instances.length} RDS instance{instances.length !== 1 ? 's' : ''} • Click row for details • Metrics refresh every 60s
              </p>
            </>
          )}
        </section>

      </div>
    </Layout>
  );
}
